/**
 * WebSocket Watch Client for real-time optimization job event streaming.
 *
 * Connects to the MutagenT server's WS /ws/optimization endpoint,
 * subscribes to a job, routes typed events to callbacks, handles
 * reconnection with exponential backoff, and cleans up on SIGINT.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Payload shape for stage result data (enriched by server). */
export interface StageResultPayload {
  type: string;
  [key: string]: unknown;
}

/**
 * Structured scorecard data shape forwarded from the server on scorecard:update events.
 * Mirrors ScorecardData from framework/metatuner/output/scorecard.ts — kept as a
 * partial interface so the CLI doesn't depend on mutagent-core at runtime.
 */
export interface ScorecardData {
  jobId: string;
  iteration: number;
  totalIterations: number | null;
  timestamp: string;
  stage: string;
  scores: {
    bestScore: number;
    currentScore: number;
    targetScore: number | null;
  };
  criteria: {
    name: string;
    scoreAvg: number;
    passRate: number;
  }[];
  costs: {
    iterationUsd: number;
    cumulativeUsd: number;
  };
  durations: {
    iterationMs: number;
    stageBreakdown: Record<string, number>;
  };
  nextAction: 'continue' | 'stop-converged' | 'stop-budget' | 'stop-stagnation' | 'stop-max-iter';
}

/** Payload carried by scorecard:update events. */
export interface ScorecardUpdatePayload {
  jobId: string;
  iteration: number;
  scorecard?: ScorecardData;          // present on success
  renderedScorecard?: string;          // present on success
  error?: {                            // present on failure
    type: 'build-failed' | 'render-failed' | 'state-malformed';
    message: string;
  };
}

export interface WatchClientOptions {
  baseUrl: string;
  jobId: string;
  workspaceId: string;
  token: string;
  onStageComplete: (stage: string, iteration: number, data: StageResultPayload) => void;
  onIterationComplete: (iteration: number, bestScore: number) => void;
  onJobComplete: (finalScore: number) => void;
  onError: (error: Error) => void;
  /** Called when a scorecard:update event arrives (optional). */
  onScorecardUpdate?: (payload: ScorecardUpdatePayload) => void;
}

export interface WatchClient {
  connect(): Promise<void>;
  close(): void;
}

/** Shape of a server-sent event wrapper. */
interface ServerEvent {
  type: string;
  jobId?: string;
  event?: {
    id?: number;
    eventType: string;
    iteration?: number;
    stage?: string;
    data?: Record<string, unknown> | null;
    createdAt?: string;
    timestamp?: string;
    /** Present when eventType === 'scorecard:update'. */
    scorecardPayload?: ScorecardUpdatePayload;
  };
  error?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Derive ws:// or wss:// URL from an http(s) base URL. */
function toWsUrl(baseUrl: string): string {
  const url = baseUrl.replace(/\/+$/, '');
  if (url.startsWith('https://')) return url.replace('https://', 'wss://');
  if (url.startsWith('http://')) return url.replace('http://', 'ws://');
  // Already ws(s)
  if (url.startsWith('wss://') || url.startsWith('ws://')) return url;
  // Fallback: assume ws
  return `ws://${url}`;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createWatchClient(opts: WatchClientOptions): WatchClient {
  const wsUrl = `${toWsUrl(opts.baseUrl)}/ws/optimization`;

  let ws: WebSocket | null = null;
  let closed = false;
  let lastEventId = 0;
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let pingInterval: ReturnType<typeof setInterval> | null = null;

  // SIGINT handler reference so we can remove it on close.
  const sigintHandler = (): void => {
    close();
  };

  // ------------------------------------------------------------------
  // Internal helpers
  // ------------------------------------------------------------------

  function schedulePing(): void {
    clearPing();
    pingInterval = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30_000);
  }

  function clearPing(): void {
    if (pingInterval !== null) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
  }

  function scheduleReconnect(): void {
    if (closed) return;
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), 10_000);
    reconnectAttempt++;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void doConnect();
    }, delay);
  }

  // ------------------------------------------------------------------
  // Event routing
  // ------------------------------------------------------------------

  function handleMessage(raw: string): void {
    let msg: ServerEvent;
    try {
      msg = JSON.parse(raw) as ServerEvent;
    } catch {
      return; // Ignore malformed messages
    }

    // Track event id for reconnect replay
    if (msg.type === 'event' && msg.event?.id !== undefined) {
      lastEventId = msg.event.id;
    }

    if (msg.type === 'error') {
      opts.onError(new Error(msg.error ?? 'Unknown WebSocket error'));
      return;
    }

    if (msg.type !== 'event' || !msg.event) return;

    const { eventType, iteration, stage, data, scorecardPayload } = msg.event;

    switch (eventType) {
      case 'stage:completed':
        opts.onStageComplete(
          stage ?? 'unknown',
          iteration ?? 0,
          (data?.stageResult ?? data ?? { type: 'unknown' }) as StageResultPayload,
        );
        break;

      case 'iteration:completed':
        opts.onIterationComplete(
          iteration ?? 0,
          (data?.score ?? data?.highestScore ?? 0) as number,
        );
        break;

      case 'job:completed': {
        const finalScore = (data?.bestScore ?? 0) as number;
        opts.onJobComplete(finalScore);
        close();
        break;
      }

      case 'job:failed':
        opts.onError(
          new Error((data?.error as string | undefined) ?? 'Optimization job failed'),
        );
        close();
        break;

      case 'scorecard:update':
        // Forward both success payloads and error sentinel payloads to the handler.
        // The handler is responsible for deciding how to render each case.
        if (scorecardPayload && opts.onScorecardUpdate) {
          opts.onScorecardUpdate(scorecardPayload);
        }
        break;

      default:
        // Ignore other event types (stage:started, score:updated, etc.)
        break;
    }
  }

  // ------------------------------------------------------------------
  // Connection
  // ------------------------------------------------------------------

  function doConnect(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (closed) {
        reject(new Error('Client is closed'));
        return;
      }

      try {
        ws = new WebSocket(wsUrl, {
          headers: {
            'x-api-key': opts.token,
            'x-workspace-id': opts.workspaceId,
          },
        } as unknown as string[]);
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }

      let settled = false;

      ws.onopen = (): void => {
        reconnectAttempt = 0;

        // Send subscribe message
        ws?.send(
          JSON.stringify({
            type: 'subscribe',
            jobId: opts.jobId,
            workspaceId: opts.workspaceId,
            afterEventId: lastEventId,
          }),
        );

        schedulePing();

        if (!settled) {
          settled = true;
          resolve();
        }
      };

      ws.onmessage = (event: MessageEvent): void => {
        handleMessage(typeof event.data === 'string' ? event.data : String(event.data));
      };

      ws.onerror = (): void => {
        if (!settled) {
          settled = true;
          reject(new Error('WebSocket error'));
        }
      };

      ws.onclose = (): void => {
        clearPing();
        if (!settled) {
          settled = true;
          reject(new Error('WebSocket closed before open'));
        }
        if (!closed) {
          scheduleReconnect();
        }
      };
    });
  }

  // ------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------

  async function connect(): Promise<void> {
    process.on('SIGINT', sigintHandler);
    await doConnect();
  }

  function close(): void {
    if (closed) return;
    closed = true;

    clearPing();

    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    process.removeListener('SIGINT', sigintHandler);

    if (ws && ws.readyState === WebSocket.OPEN) {
      // Send unsubscribe before closing
      ws.send(
        JSON.stringify({ type: 'unsubscribe', jobId: opts.jobId }),
      );
      ws.close();
    }

    ws = null;
  }

  return { connect, close };
}
